import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useProjectOwners, type ProjectOwner } from "@/hooks/use-project-owners";
import { useSalesUnits, type SalesUnit } from "@/hooks/use-sales-units";
import type {
  DashboardProject,
  DashboardOwner,
  BfTargetSalesUnit,
} from "@/lib/dashboard-bf-calc";

/**
 * Raw-data layer for the sales dashboard (F1 KPI cards + F2 panels). Fetches the
 * rows the pure dashboard-bf-calc / dashboard-panels-calc functions consume —
 * projects (BF fields + name/code for panels), their closed-date from the linked
 * accepted offer — and reuses useProjectOwners + useSalesUnits. Returns mapped
 * calc input types; all math lives in the lib calc modules (read-only on
 * canonical bf_actual/bf_estimated).
 */

interface RawProject {
  id: string;
  name: string;
  project_code: string | null;
  is_internal: boolean | null;
  project_status: string | null;
  total_amount: number | null;
  bf_estimated: number | null;
  bf_actual: number | null;
  completed_at: string | null;
  created_at: string;
  estimated_deadline: string | null;
  customer_id: string | null;
  customers: { name: string } | null;
}

const PROJECT_COLUMNS =
  "id, name, project_code, is_internal, project_status, total_amount, bf_estimated, bf_actual, completed_at, created_at, estimated_deadline, customer_id, customers(name)";

export interface SalesPerson {
  userId: string;
  name: string;
  avatarUrl: string | null;
}

export interface ProjectMeta {
  name: string;
  code: string | null;
}

/** Map accepted offers to a project_id → earliest accepted_at lookup. */
export function buildClosedDateMap(
  offers: { project_id: string | null; accepted_at: string | null }[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const o of offers) {
    if (!o.project_id || !o.accepted_at) continue;
    const existing = map.get(o.project_id);
    if (!existing || o.accepted_at < existing) map.set(o.project_id, o.accepted_at);
  }
  return map;
}

/** Attach each project's closed date (offer.accepted_at) for calc input. */
export function mapDashboardProjects(
  raw: RawProject[],
  closedDates: Map<string, string>,
): DashboardProject[] {
  return raw.map((p) => ({
    id: p.id,
    project_code: p.project_code,
    is_internal: p.is_internal,
    project_status: p.project_status ?? "",
    total_amount: p.total_amount,
    bf_estimated: p.bf_estimated,
    bf_actual: p.bf_actual,
    completed_at: p.completed_at,
    created_at: p.created_at,
    estimated_deadline: p.estimated_deadline,
    offer_accepted_at: closedDates.get(p.id) ?? null,
    customer_id: p.customer_id,
  }));
}

/** customer_id → customer name, for Hall of Fame's best-customer panel (F3). */
export function buildCustomerMeta(raw: RawProject[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const p of raw) {
    if (p.customer_id && p.customers?.name) map.set(p.customer_id, p.customers.name);
  }
  return map;
}

/** id → {name, code} for panels that display project identity (F2.3). */
export function buildProjectMeta(raw: RawProject[]): Map<string, ProjectMeta> {
  return new Map(raw.map((p) => [p.id, { name: p.name, code: p.project_code }]));
}

/** project_owners → calc DashboardOwner, including unit attribution. */
export function mapDashboardOwners(owners: ProjectOwner[]): DashboardOwner[] {
  return owners.map((o) => ({
    project_id: o.project_id,
    user_id: o.user_id,
    sales_unit_id: o.sales_unit_id ?? null,
    ownership_pct: o.ownership_pct,
  }));
}

/** sales_units (+members) → deriveBfTarget input. */
export function mapBfTargetUnits(units: SalesUnit[]): BfTargetSalesUnit[] {
  return units.map((u) => ({
    id: u.id,
    name: u.name,
    bf_target_mnok: u.bf_target_mnok,
    year: u.year,
    memberUserIds: (u.members ?? []).map((m) => m.user_id),
  }));
}

/** Distinct sales owners (the sales team) across all units, sorted by name. */
export function collectSalesPeople(units: SalesUnit[]): SalesPerson[] {
  const byId = new Map<string, SalesPerson>();
  for (const u of units) {
    for (const m of u.members ?? []) {
      if (!byId.has(m.user_id)) {
        byId.set(m.user_id, {
          userId: m.user_id,
          name: m.user?.full_name ?? "Ukjent",
          avatarUrl: m.user?.avatar_url ?? null,
        });
      }
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, "nb"));
}

interface ProjectsQueryResult {
  projects: DashboardProject[];
  projectMeta: Map<string, ProjectMeta>;
  customerMeta: Map<string, string>;
}

export interface SalesDashboardData {
  projects: DashboardProject[];
  owners: DashboardOwner[];
  salesUnits: BfTargetSalesUnit[];
  people: SalesPerson[];
  projectMeta: Map<string, ProjectMeta>;
  customerMeta: Map<string, string>;
  isLoading: boolean;
  isError: boolean;
}

export function useSalesDashboardData(opts: { refetchInterval?: number } = {}): SalesDashboardData {
  const ownersQuery = useProjectOwners();
  const unitsQuery = useSalesUnits();

  const projectsQuery = useQuery({
    queryKey: ["sales-dashboard-projects"],
    refetchInterval: opts.refetchInterval,
    queryFn: async (): Promise<ProjectsQueryResult> => {
      // Paginate to avoid Supabase's 1000-row default cap.
      const rawProjects: RawProject[] = [];
      const PAGE = 1000;
      let from = 0;
      for (;;) {
        const { data, error } = await supabase
          .from("projects")
          .select(PROJECT_COLUMNS)
          .is("deleted_at", null)
          .range(from, from + PAGE - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        rawProjects.push(...(data as unknown as RawProject[]));
        if (data.length < PAGE) break;
        from += PAGE;
      }

      const { data: offers, error: offersErr } = await supabase
        .from("offers")
        .select("project_id, accepted_at")
        .eq("status", "accepted")
        .not("project_id", "is", null);
      if (offersErr) throw offersErr;

      return {
        projects: mapDashboardProjects(rawProjects, buildClosedDateMap(offers ?? [])),
        projectMeta: buildProjectMeta(rawProjects),
        customerMeta: buildCustomerMeta(rawProjects),
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    projects: projectsQuery.data?.projects ?? [],
    owners: mapDashboardOwners(ownersQuery.data ?? []),
    salesUnits: mapBfTargetUnits(unitsQuery.data ?? []),
    people: collectSalesPeople(unitsQuery.data ?? []),
    projectMeta: projectsQuery.data?.projectMeta ?? new Map(),
    customerMeta: projectsQuery.data?.customerMeta ?? new Map(),
    isLoading: projectsQuery.isLoading || ownersQuery.isLoading || unitsQuery.isLoading,
    isError: projectsQuery.isError || ownersQuery.isError || unitsQuery.isError,
  };
}
